import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';

import { useSplitFulfillment } from './OrderFulfillments';
import { Pagination } from './Pagination';
import { SplitFulfillmentCTA } from './buttons/SplitFulfillmentCTA';
import { isOpsAdmin, isAllModuleAccessUser } from '../pages/constants';
import { FinancialHeaders } from '../tableHeaders';
import { BOTTOM_TOAST } from '../utils';

interface TableProps<T> {
  header: string[];
  data: T[];
  pageSize?: number;
  paginatorsPerPageBlock?: number;
  internalPagination?: boolean;
  tdClass?: string;
  currency?: string;
}

const usePaginationPages = (
  data: any[],
  length: number,
  pageSize: number,
  paginatorsPerPageBlock = 5
) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = useMemo(() => {
    return Math.ceil(length / pageSize);
  }, [length, pageSize]);

  // Correct an out-of-range or zero-results page synchronously during render (instead of in
  // a useEffect), so a shrinking dataset never renders one frame with the stale page's empty
  // slice, and a stale page from a zero-results state can't silently fall back in range once
  // results return.
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const effectiveCurrentPage = totalPages === 0 || currentPage > totalPages ? 1 : currentPage;
  if (effectiveCurrentPage !== currentPage) {
    setCurrentPage(effectiveCurrentPage);
  }

  const canGo = useMemo(() => {
    return {
      next: effectiveCurrentPage < totalPages,
      previous: effectiveCurrentPage - 1 > 0,
    };
  }, [effectiveCurrentPage, totalPages]);

  const pages = useMemo(() => {
    const start =
      Math.floor((effectiveCurrentPage - 1) / paginatorsPerPageBlock) * paginatorsPerPageBlock;
    const end =
      start + paginatorsPerPageBlock > totalPages ? totalPages : start + paginatorsPerPageBlock;
    return Array.from({ length: end - start }, (_, i) => start + i + 1);
  }, [effectiveCurrentPage, paginatorsPerPageBlock, totalPages]);

  const goTo = (pg: number) => {
    setCurrentPage(pg);
  };

  const goNext = useCallback(() => {
    if (canGo.next) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [canGo]);

  const goPrev = useCallback(() => {
    if (canGo.previous) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [canGo]);

  const currentData = data.slice(
    (effectiveCurrentPage - 1) * pageSize,
    pageSize * effectiveCurrentPage
  );
  const renderPagination = data.length > pageSize;

  return {
    canGo,
    currentPage: effectiveCurrentPage,
    pages,
    goTo,
    goNext,
    goPrev,
    currentData,
    renderPagination,
    countOfResults: length,
  };
};

export const Table = <T extends Record<string, any>>({
  header,
  data,
  pageSize = 25,
  paginatorsPerPageBlock,
  internalPagination = true,
  tdClass,
  currency,
}: TableProps<T>) => {
  const { currentData, renderPagination, ...forPagination } = usePaginationPages(
    data,
    data.length,
    pageSize,
    paginatorsPerPageBlock
  );
  if (currentData.length) {
    return (
      <>
        <div className="overflow-x-auto rounded border">
          <table
            className="min-w-full table-auto text-center border-separate"
            style={{ borderSpacing: 0 }}
          >
            <thead className="bg-slate50">
              <tr>
                {header.map((column, index) => (
                  <th
                    key={index}
                    className={`px-4 py-2 whitespace-nowrap ${
                      index === 0
                        ? 'sticky left-0 bg-slate50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                        : ''
                    }`}
                  >
                    {column}
                    {currency && FinancialHeaders.includes(column) ? ` (${currency})` : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentData.map((item, index) => (
                <tr key={index} className="bg-white ">
                  {header.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={`px-4 py-2 border-[1px] border-slate-50 ${
                        FinancialHeaders.includes(column) ? 'whitespace-nowrap' : ''
                      } ${tdClass} ${
                        colIndex === 0
                          ? `sticky left-0 bg-white z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`
                          : ''
                      }`}
                    >
                      {Array.isArray(item[column]) ? (
                        <ul>
                          {item[column].map((listItem: string, listIndex: number) => (
                            <li key={String(listIndex)} className="whitespace-nowrap text-left">
                              {listItem}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        item[column]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {internalPagination && renderPagination && <Pagination {...forPagination} />}
      </>
    );
  }
  return null;
};

export const FulfillmentsTable = <T extends Record<string, any>>({
  header,
  data,
  pageSize = 25,
  paginatorsPerPageBlock,
  internalPagination = true,
  tdClass,
  currency,
}: TableProps<T>) => {
  const { currentData, renderPagination, ...forPagination } = usePaginationPages(
    data,
    data.length,
    pageSize,
    paginatorsPerPageBlock
  );
  const showSplitFulfillmentCTA = isOpsAdmin() || isAllModuleAccessUser();
  const splitFulfillment = useSplitFulfillment(); // Get the function from the hook

  const allFulfillmentsByID = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const index in data) {
      const fulfillment = data[index];
      const id = fulfillment['Fulfillment ID'];
      if (id in map) {
        map[id].push(fulfillment);
      } else {
        map[id] = [fulfillment];
      }
    }
    return map;
  }, [data]);

  useEffect(() => {
    const tableData = Object.keys(allFulfillmentsByID);

    // Gets the highest tag (-S#) from fulfillment ID
    const findHighestTag = (baseId: string): string => {
      const extractSuffix = (id: string): number => {
        const suffix = id.split('-S').pop();
        return suffix ? parseInt(suffix, 10) : NaN; // Return NaN for invalid parsing
      };

      const matchingIds = tableData.filter((id) => id.startsWith(`${baseId}-S`));
      if (matchingIds.length === 0) {
        return baseId;
      }

      const sortedIds = matchingIds.sort((a, b) => extractSuffix(b) - extractSuffix(a));

      return sortedIds[0];
    };

    const savedToast = localStorage.getItem('toastMessage');

    if (savedToast) {
      const { message: fulfillmentId, type } = JSON.parse(savedToast);

      if (fulfillmentId) {
        const highestTag = findHighestTag(fulfillmentId);
        const toastMsg = `Fulfillment ${highestTag} successfully split off from fulfillment ${fulfillmentId}`;

        if (type === 'success') {
          toast.success(toastMsg, BOTTOM_TOAST);
        } else if (type === 'error') {
          toast.error(toastMsg);
        }
      }
      localStorage.removeItem('toastMessage');
    }
  }, [allFulfillmentsByID]);

  const seenFulfillmentIDs = new Set();

  if (currentData.length) {
    return (
      <div className="overflow-x-auto rounded border">
        <table
          className="min-w-full table-auto text-center border-separate"
          style={{ borderSpacing: 0 }}
        >
          <thead className="bg-slate50">
            <tr>
              {header.map((column, index) => (
                <th
                  key={index}
                  className={`px-4 py-2 whitespace-nowrap border-[1px] border-slate-50 ${
                    index === 0
                      ? 'sticky left-0 bg-slate50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                      : ''
                  }`}
                >
                  {column}
                  {currency && FinancialHeaders.includes(column) ? ` (${currency})` : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((item, index) => (
              <tr key={index} className="bg-white">
                {header.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`px-4 py-2 border-[1px] border-slate-50 ${
                      FinancialHeaders.includes(column) ? 'whitespace-nowrap' : ''
                    } ${tdClass} ${
                      colIndex === 0
                        ? `sticky left-0 bg-white z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`
                        : ''
                    }`}
                  >
                    {Array.isArray(item[column]) ? (
                      <ul>
                        {item[column].map((listItem: string, listIndex: number) => (
                          <li key={String(listIndex)} className="whitespace-nowrap text-left">
                            {listItem}
                          </li>
                        ))}
                      </ul>
                    ) : column === 'Fulfillment ID' ? (
                      <div>
                        {(() => {
                          if (seenFulfillmentIDs.has(item[column])) {
                            return '--';
                          } else {
                            seenFulfillmentIDs.add(item[column]);
                            return item.Is_Splittable &&
                              item[column] !== '--' &&
                              showSplitFulfillmentCTA ? (
                              <div className="flex flex-col">
                                {item[column]}
                                <SplitFulfillmentCTA
                                  className="px-[1px] py-[1px] ml-1"
                                  variant="secondary"
                                  textClassName="text-sm font-normal"
                                  label="Split Fulfillment"
                                  onClick={() => splitFulfillment(item[column])}
                                />
                              </div>
                            ) : (
                              item[column]
                            );
                          }
                        })()}
                      </div>
                    ) : (
                      item[column]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {internalPagination && renderPagination && <Pagination {...forPagination} />}
      </div>
    );
  }
  return null;
};
